/** @jsxImportSource react */
import { Button } from '@react/system';
import ToggleButton from '@react/system/ToggleButton';
import { Chevron } from '@sly/icons/react';
import cx from 'classnames';
import { number, object, string } from 'proptypes';
import React, { Component } from 'react';

const ChevronLink = ({ flip, className, ...props }) => {
  return (
    <Button
      variant='neutral'
      pallete='slate'
      onClick={() => window.scrollTo(0, 0)}
      className={cx(
        'mr-m flex h-[32px] w-[32px]',
        'min-h-[unset] !px-0 !py-0 leading-normal',
        'mr-0 md:mr-m',
        className && className
      )}
      {...props}
      data-tp-id='gen-Button-0c36a9dd-8042-432b-84ac-67f38606e571'
    >
      <Chevron
        className={cx('m-auto block', flip ? '-rotate-90' : 'rotate-90')}
        size='m'
        color='slate'
      />
    </Button>
  );
};

const PageLink = ({ children, ...props }) => {
  return (
    <span
      className='hidden md:block'
      data-tp-id='gen-span-e1a490f7-5609-422f-97a7-7235f2a3447e'
    >
      <ToggleButton
        {...props}
        className={cx('mr-m h-[32px] min-h-[unset] w-[32px] p-0')}
      >
        {children}
      </ToggleButton>
    </span>
  );
};

export default class Pagination extends Component {
  static propTypes = {
    current: number.isRequired,
    total: number.isRequired,
    margin: number.isRequired,
    range: number.isRequired,
    basePath: string.isRequired,
    pageParam: string.isRequired,
    className: string,
    palette: string,
    eventProps: object,
  };

  static defaultProps = {
    current: 0,
    margin: 1,
    range: 5,
    palette: 'primary',
    eventProps: {},
  };

  prevButton() {
    const { current, basePath, pageParam, eventProps } = this.props;

    if (current <= 1) return null;

    let delim = '?';
    if (basePath.indexOf(delim) > -1) {
      delim = '&';
    }

    const prev = current - 1;
    const prevHref =
      prev === 1 ? basePath : `${basePath}${delim}${pageParam}=${prev}`;

    return (
      <div data-tp-id='gen-div-f19ae0cb-3530-45f4-8327-81964fa53626'>
        <ChevronLink
          to={prevHref}
          flip
          eventProps={{
            ...eventProps,
            text: 'icon:chevron-previous',
          }}
        />
      </div>
    );
  }

  nextButton() {
    const { current, total, basePath, pageParam, eventProps } = this.props;

    if (current >= total) {
      return null;
    }

    let delim = '?';
    if (basePath && basePath.indexOf(delim) > -1) {
      delim = '&';
    }

    const next = current + 1;
    const nextHref = `${basePath}${delim}${pageParam}=${next}`;

    return (
      <ChevronLink
        to={nextHref}
        eventProps={{
          ...eventProps,
          text: 'icon:chevron-next',
        }}
      />
    );
  }

  ellipsis = (index) => {
    return (
      <div
        className={cx(
          'inline-block items-center justify-center',
          'h-xs text-slate-base',
          'cursor-default border-white',
          'mr-m h-[32px] w-[32px]',
          'hidden md:block'
        )}
        key={index}
        data-tp-id='gen-div-a34787c4-920a-4294-aa5b-239dc6cb6c0f'
      >
        ...
      </div>
    );
  };

  pageButton(index) {
    const { current, basePath, pageParam, eventProps } = this.props;
    const sel = current === index;
    let delim = '?';
    if (basePath && basePath.indexOf(delim) > -1) {
      delim = '&';
    }
    const pageHref =
      index === 1 ? basePath : `${basePath}${delim}${pageParam}=${index}`;
    return (
      <PageLink
        kind='label'
        key={index}
        to={pageHref}
        onChange={() => window.scrollTo(0, 0)}
        value={sel}
        eventProps={{ ...eventProps, text: index }}
      >
        {index}
      </PageLink>
    );
  }

  pagination() {
    // loop inspired by react-paginate
    const {
      current: stringCurrent,
      range,
      total: fractionTotal,
      margin,
    } = this.props;

    const current = parseInt(stringCurrent, 10);
    const total = Math.ceil(fractionTotal);

    let leftSide = range / 2;
    let rightSide = range - leftSide;

    if (current > total - range / 2) {
      rightSide = total - current;
      leftSide = range - rightSide;
    } else if (current < range / 2) {
      leftSide = current;
      rightSide = range - leftSide;
    }

    let page;
    let breakView;

    const items = [];
    for (let index = 1; index <= total; index += 1) {
      page = index;

      if (page <= margin) {
        items.push(this.pageButton(index));
      } else if (page > total - margin) {
        items.push(this.pageButton(index));
      } else if (index >= current - leftSide && index <= current + rightSide) {
        items.push(this.pageButton(index));
      } else if (items[items.length - 1] !== breakView) {
        breakView = this.ellipsis(index);
        items.push(breakView);
      }
    }

    return items;
  }

  render() {
    const { className, current, total } = this.props;
    return (
      <div
        className={cx('flex flex-wrap', className)}
        data-tp-id='gen-div-6fe79290-07b6-41f8-889f-0257d5ea4e80'
      >
        {this.prevButton()}
        {this.pagination()}
        <div
          className='inline-flex min-w-[242px] items-center justify-center md:hidden'
          data-tp-id='gen-div-ee701724-2720-4414-8504-9d0850c5ed8d'
        >
          Page {current} of {Math.ceil(total)}
        </div>
        {this.nextButton()}
      </div>
    );
  }
}
